import type { ModelProps, ModelRegistry } from '../types';

function BananaTree({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Short thick trunk */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.2, 1, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#9ACD32' : '#5a7a3a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Large banana leaves */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * Math.PI * 2) / 6;
        return (
          <group key={i} position={[0, 1, 0]} rotation={[0.6, angle, 0]}>
            <mesh position={[0, 0.3, 0.4]} rotation={[-0.2, 0, 0]}>
              <planeGeometry args={[0.25, 0.9]} />
              <meshStandardMaterial
                color={world === 'real' ? '#32CD32' : '#2d6a2d'}
                roughness={0.6}
                side={2}
                transparent={isOccluding}
                opacity={opacity}
              />
            </mesh>
          </group>
        );
      })}
      {/* Banana bunch */}
      <group position={[0.2, 0.8, 0]} rotation={[0, 0, 0.5]}>
        {[0, 1, 2, 3, 4].map((i) => (
          <mesh key={i} position={[0, -i * 0.06, (i % 2) * 0.05]} rotation={[0, 0, 0.3]}>
            <capsuleGeometry args={[0.025, 0.1, 4, 8]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FFD700' : '#b8a030'}
              roughness={0.5}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function GiantFern({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Central stem */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 0.6, 6]} />
        <meshStandardMaterial
          color={world === 'real' ? '#556B2F' : '#3a4a2a'}
          roughness={0.8}
        />
      </mesh>
      {/* Fern fronds spiraling outward */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i * Math.PI * 2) / 8 + i * 0.1;
        const height = 0.4 + (i % 3) * 0.15;
        return (
          <group key={i} position={[0, height, 0]} rotation={[0.8 - (i % 3) * 0.2, angle, 0]}>
            {/* Main frond */}
            <mesh position={[0, 0, 0.35]}>
              <coneGeometry args={[0.12, 0.7, 5]} />
              <meshStandardMaterial
                color={world === 'real' ? '#228B22' : '#1a5a1a'}
                roughness={0.7}
                side={2}
                transparent={isOccluding}
                opacity={opacity}
              />
            </mesh>
            {/* Sub-fronds */}
            {[0, 1, 2].map((j) => (
              <mesh key={j} position={[((j - 1) * 0.08), 0, 0.15 + j * 0.1]} rotation={[(j - 1) * 0.2, 0, 0]}>
                <coneGeometry args={[0.04, 0.2, 3]} />
                <meshStandardMaterial
                  color={world === 'real' ? '#32CD32' : '#2d7a2d'}
                  roughness={0.7}
                  side={2}
                />
              </mesh>
            ))}
          </group>
        );
      })}
    </group>
  );
}

function TropicalFlower({ world }: ModelProps) {
  return (
    <group>
      {/* Stem */}
      <mesh position={[0, 0.2, 0]} castShadow>
        <cylinderGeometry args={[0.02, 0.03, 0.4, 6]} />
        <meshStandardMaterial color="#2E8B57" roughness={0.7} />
      </mesh>
      {/* Leaves */}
      {[0, 1].map((i) => (
        <mesh key={i} position={[(i - 0.5) * 0.15, 0.15, 0]} rotation={[0.3, i * Math.PI, 0.3 * (i - 0.5)]}>
          <planeGeometry args={[0.12, 0.08]} />
          <meshStandardMaterial color="#228B22" side={2} roughness={0.6} />
        </mesh>
      ))}
      {/* Flower petals */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i * Math.PI * 2) / 5;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.08, 0.42, Math.sin(angle) * 0.08]} rotation={[0.5, angle, 0]}>
            <planeGeometry args={[0.1, 0.15]} />
            <meshStandardMaterial
              color={world === 'real' ? '#FF1493' : '#9932CC'}
              emissive={world === 'spectral' ? '#9932CC' : undefined}
              emissiveIntensity={world === 'spectral' ? 0.3 : 0}
              side={2}
              roughness={0.4}
            />
          </mesh>
        );
      })}
      {/* Center */}
      <mesh position={[0, 0.42, 0]}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFD700' : '#DDA0DD'} />
      </mesh>
      {/* Stamen */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.15, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#FF6347' : '#DA70D6'} />
      </mesh>
    </group>
  );
}

function JungleVine({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      {/* Main vine - curved */}
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[(i - 1) * 0.1, 0.8 - i * 0.3, i * 0.05]} rotation={[0.1 * i, 0, 0.15 * (i - 1)]} castShadow>
          <cylinderGeometry args={[0.03, 0.04, 0.6, 6]} />
          <meshStandardMaterial
            color={world === 'real' ? '#556B2F' : '#3a5a3a'}
            roughness={0.8}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
      {/* Large leaves along vine */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={i} position={[Math.sin(i * 1.5) * 0.15, 1 - i * 0.18, Math.cos(i * 1.5) * 0.1]} rotation={[0.5, i * 0.8, 0.3]}>
          <planeGeometry args={[0.15, 0.1]} />
          <meshStandardMaterial
            color={world === 'real' ? '#32CD32' : '#2d6a2d'}
            side={2}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export const jungleModels: ModelRegistry = {
  banana_tree: BananaTree,
  giant_fern: GiantFern,
  tropical_flower: TropicalFlower,
  jungle_vine: JungleVine,
};
